/**
 * Shared helpers for admin impersonation URL params
 * (`adminClientId`, `adminCompanyId`, `adminCustomerId`).
 *
 * AdminClientsContext lives above RouterProvider and reads/writes via the History API.
 * TanStack Router validates the same param names in `__root.tsx` validateSearch.
 * UI selectors should prefer `setSelectedClientId` / `navigate` with these helpers
 * so URL state stays consistent.
 */

export const ADMIN_CLIENT_ID_PARAM = 'adminClientId';
export const ADMIN_COMPANY_ID_PARAM = 'adminCompanyId';
export const ADMIN_CUSTOMER_ID_PARAM = 'adminCustomerId';

export interface AdminScopeParams {
  adminClientId: string | null;
  adminCompanyId: string | null;
  adminCustomerId: string | null;
}

/** Read admin scope params from a URL or the current window location. */
export function readAdminScopeFromUrl(href?: string): AdminScopeParams {
  const source = href ?? (typeof window !== 'undefined' ? window.location.href : '');
  if (!source) {
    return { adminClientId: null, adminCompanyId: null, adminCustomerId: null };
  }

  const url = new URL(source, typeof window !== 'undefined' ? window.location.origin : undefined);
  return {
    adminClientId: url.searchParams.get(ADMIN_CLIENT_ID_PARAM),
    adminCompanyId: url.searchParams.get(ADMIN_COMPANY_ID_PARAM),
    adminCustomerId: url.searchParams.get(ADMIN_CUSTOMER_ID_PARAM)
  };
}

/**
 * Apply admin scope params to a URL. Passing `null` removes a param.
 * When client changes, pass `adminCompanyId: null` and `adminCustomerId: null` to clear stale scope.
 */
export function applyAdminScopeToUrl(
  href: string,
  scope: Partial<AdminScopeParams>
): string {
  const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : undefined);

  if ('adminClientId' in scope) {
    const clientId = scope.adminClientId;
    if (clientId && clientId.trim().length > 0) {
      url.searchParams.set(ADMIN_CLIENT_ID_PARAM, clientId);
    } else {
      url.searchParams.delete(ADMIN_CLIENT_ID_PARAM);
    }
  }

  if ('adminCompanyId' in scope) {
    const companyId = scope.adminCompanyId;
    if (companyId && companyId.trim().length > 0) {
      url.searchParams.set(ADMIN_COMPANY_ID_PARAM, companyId);
    } else {
      url.searchParams.delete(ADMIN_COMPANY_ID_PARAM);
    }
  }

  if ('adminCustomerId' in scope) {
    const customerId = scope.adminCustomerId;
    if (customerId && customerId.trim().length > 0) {
      url.searchParams.set(ADMIN_CUSTOMER_ID_PARAM, customerId);
    } else {
      url.searchParams.delete(ADMIN_CUSTOMER_ID_PARAM);
    }
  }

  return url.toString();
}
